/** @jsxRuntime automatic */
/** @jsxImportSource react */
import React, { createContext, useContext, useEffect, useId, useRef, useState } from "react"
import { KpiFormulaEditor, KpiModeTabs } from "./KpiFormulaEditor"
import {
  aggregateDashboardKpi,
  dashboardKpiDataStatus,
  dashboardKpiRequestKey,
  resolveDashboardKpi,
} from "@/datox/dashboard-kpi"
import { KpiComparisonDisplay } from "./kpi-comparison"

/** @type {import("react").Context<{
 * datasets?: Record<string, import("@/datox/dashboard-kpi").DashboardKpiDataset>,
 * locale?: string, sourceKey?: string, dashboardID?: string,
 * sourceKeys?: Record<string, string>,
 * kpiValues?: Record<string, import("@/datox/api").DatoxKpiResult>,
 * onEvaluateKpi?: (widget: Record<string, unknown>, filters: Array<Record<string, unknown>>, evidenceLimit: number) => Promise<import("@/datox/api").DatoxKpiResult>
 * }>} */
export const DashboardKpiContext = createContext({ datasets: {} })

export function kpiIssueMessage(labels, error) {
  const template = labels.errors?.[error?.code] || labels.errors?.invalid_expression || labels.error
  return template.replace(/\{(\w+)\}/g, (_, key) => String(error?.params?.[key] ?? ""))
}

export function KpiWidget(props) {
  const element = useRef(null)
  const canvas = useRef(null)
  const [size, setSize] = useState({ width: 0, height: 0, textWidth: 0 })
  const labels = props.labels.kpi
  const [remote, setRemote] = useState(null)
  const canEvaluate = !!(props.onEvaluateKpi && props.widget?.id)
  const requestKey = dashboardKpiRequestKey(props.widget || {}, props.filterWidgets || [], props.sourceKey)
  useEffect(() => {
    if (!canEvaluate || !props.widget?.data?.dataset_id || props.status !== "ready") return undefined
    // An unrelated resource reload must not clear or reevaluate the same result.
    if (remote?.key === requestKey && remote.value.status !== "error") return undefined
    let active = true
    setRemote(null)
    const timer = setTimeout(() => {
      props
        .onEvaluateKpi(props.widget, props.filterWidgets || [], 0)
        .then((value) => {
          if (active) setRemote({ key: requestKey, value })
        })
        .catch(() => {
          if (active) setRemote({ key: requestKey, value: { status: "error" } })
        })
    }, 120)
    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [requestKey, props.status, canEvaluate])
  const calculated =
    canEvaluate && (props.status === "ready" || props.status === "loading")
      ? remote?.key === requestKey
        ? remote.value
        : !props.filterWidgets?.length
          ? props.captured
          : null
      : null
  const status =
    calculated && canEvaluate
      ? calculated.status === "unavailable"
        ? "unavailable"
        : calculated.status
      : canEvaluate
        ? !props.widget?.data?.dataset_id
          ? "missing"
          : props.status !== "ready"
            ? props.status
            : "loading"
        : props.widget?.kpi_calculation
          ? "unavailable"
          : props.status === "ready"
            ? dashboardKpiDataStatus(props.table, props.data)
            : props.status
  const total =
    status === "ready"
      ? calculated && canEvaluate
        ? calculated.value
        : aggregateDashboardKpi(props.table, props.data)
      : null
  const result = total === null ? undefined : resolveDashboardKpi(total, props.style, props.locale)
  const measure = () => {
    const value = element.current?.querySelector(".datox-react-dashboard__kpi-value")
    const parent = element.current?.parentElement
    if (!value || !parent) return
    const font = getComputedStyle(value)
    canvas.current ??= document.createElement("canvas").getContext("2d")
    if (canvas.current) canvas.current.font = `${font.fontWeight} 100px ${font.fontFamily}`
    const textWidth = canvas.current ? canvas.current.measureText(value.textContent).width / 100 : 0
    const box = parent.getBoundingClientRect()
    const padding = getComputedStyle(parent)
    const width = box.width - parseFloat(padding.paddingLeft) - parseFloat(padding.paddingRight)
    const height = box.height - parseFloat(padding.paddingTop) - parseFloat(padding.paddingBottom)
    setSize((current) =>
      current.width === width && current.height === height && current.textWidth === textWidth
        ? current
        : { width, height, textWidth },
    )
  }
  useEffect(() => {
    const parent = element.current?.parentElement
    if (!parent || typeof ResizeObserver === "undefined") return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(parent)
    void document.fonts?.ready.then(measure)
    return () => observer.disconnect()
  }, [status, result?.status])
  useEffect(measure, [status, result?.status, result?.displayValue, props.locale, props.font])
  if (status !== "ready")
    return (
      <div
        className="datox-react-dashboard__kpi-state"
        role={status === "error" || status === "invalid" ? "alert" : "status"}
        aria-busy={status === "loading"}
        data-kpi-state={status}
      >
        {status === "invalid" ? kpiIssueMessage(labels, calculated?.error) : labels[status] || labels.missing}
      </div>
    )
  if (result.status !== "ready")
    return (
      <div className="datox-react-dashboard__kpi-state" role="alert" data-kpi-state="error">
        {Number.isFinite(total) ? labels.invalid : labels.error}
      </div>
    )
  const availableWidth = Math.max(80, size.width || (props.layout.w || 320) - 52)
  const availableHeight = size.height || Math.max(14, (props.layout.h || 150) - 42)
  const heightSize = Math.max(12, Math.min(112, Math.round((availableHeight - (result.target ? 66 : 0)) * 0.78)))
  const widthSize = Math.max(
    12,
    Math.floor(availableWidth / (size.textWidth || Math.max(3, result.displayValue.length) * 0.62)),
  )
  const valueSize = Math.max(12, Math.min(heightSize, widthSize, result.layout === "compact" ? 32 : 112))
  const target = result.target
  return (
    <div
      ref={element}
      className={`datox-react-dashboard__kpi datox-react-dashboard__kpi--${result.layout}`}
      style={{ textAlign: props.style.textAlign || "left" }}
      data-kpi-state="ready"
    >
      <div
        className="datox-react-dashboard__kpi-value"
        aria-label={labels.actual}
        style={{ fontSize: props.valueScale === undefined ? valueSize : Math.max(8, valueSize * props.valueScale / 100) }}
        title={result.displayValue}
      >
        {result.displayValue}
      </div>
      {target ? (
        <div className="datox-react-dashboard__kpi-target">
          <div className="datox-react-dashboard__kpi-target-label">
            {labels.target}: {target.displayValue}
          </div>
          <div
            className="datox-react-dashboard__kpi-progress"
            role="progressbar"
            aria-label={labels.progress}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={target.fill}
            aria-valuetext={`${labels.actual}: ${result.displayValue}; ${labels.target}: ${target.displayValue}; ${target.text}`}
          >
            {target.visual === "ring" ? (
              <svg className="datox-react-dashboard__kpi-ring" viewBox="0 0 40 40" aria-hidden="true">
                <circle cx="20" cy="20" r="16" fill="none" stroke="#e4e7ec" strokeWidth="4" />
                <circle
                  cx="20"
                  cy="20"
                  r="16"
                  fill="none"
                  stroke={props.accent || "#2f80ed"}
                  strokeWidth="4"
                  strokeDasharray={`${(target.fill * Math.PI * 32) / 100} ${Math.PI * 32}`}
                  transform="rotate(-90 20 20)"
                />
              </svg>
            ) : (
              <div className="datox-react-dashboard__kpi-bar" aria-hidden="true">
                <div style={{ width: `${target.fill}%`, backgroundColor: props.accent || "#2f80ed" }} />
              </div>
            )}
            <span>{target.text}</span>
          </div>
        </div>
      ) : null}
      {props.comparison ? (
        <KpiComparisonDisplay labels={props.labels} widget={props.widget} result={props.comparison} />
      ) : null}
    </div>
  )
}

export function KpiDisplaySettings(props) {
  const labels = props.labels.kpi
  const style = props.style
  const percent = style.valueFormat === "percent"
  const scaling = style.scale && style.scale !== "none" ? style.scale : style.notation || "standard"
  const target = style.target
  return (
    <>
      <label>
        <span>{props.labels.valueFormat}</span>
        <select
          className="datox-react-dashboard__select"
          value={style.valueFormat || "number"}
          onChange={(event) => props.onChange({ valueFormat: event.currentTarget.value })}
        >
          <option value="number">{props.labels.numberFormat}</option>
          <option value="integer">{props.labels.integerFormat}</option>
          <option value="currency">{labels.currencyFormat}</option>
          <option value="percent">{props.labels.percentFormat}</option>
        </select>
      </label>
      <label>
        <span>{props.labels.decimalPlaces}</span>
        <input
          className="datox-react-dashboard__input"
          type="number"
          min="0"
          max="6"
          step="1"
          disabled={style.valueFormat === "integer"}
          value={style.valueFormat === "integer" ? 0 : (style.decimalPlaces ?? 2)}
          onChange={(event) =>
            props.onChange({ decimalPlaces: Math.min(6, Math.max(0, Math.trunc(Number(event.currentTarget.value)))) })
          }
        />
      </label>
      {style.valueFormat === "currency" ? (
        <label>
          <span>{labels.currency}</span>
          <input
            className="datox-react-dashboard__input"
            maxLength={3}
            value={style.currency ?? "USD"}
            onChange={(event) => props.onChange({ currency: event.currentTarget.value.toUpperCase() })}
          />
        </label>
      ) : null}
      {percent ? (
        <label>
          <span>{labels.percentScale}</span>
          <select
            className="datox-react-dashboard__select"
            value={style.percentScale || "points"}
            onChange={(event) => props.onChange({ percentScale: event.currentTarget.value })}
          >
            <option value="points">{labels.points}</option>
            <option value="ratio">{labels.ratio}</option>
          </select>
        </label>
      ) : null}
      <label>
        <span>{labels.unit}</span>
        <input
          className="datox-react-dashboard__input"
          maxLength={24}
          value={style.unit || ""}
          onChange={(event) => props.onChange({ unit: event.currentTarget.value })}
        />
      </label>
      <label>
        <span>{labels.notation}</span>
        <select
          className="datox-react-dashboard__select"
          disabled={percent}
          value={percent ? "standard" : scaling}
          onChange={(event) =>
            props.onChange(
              ["standard", "compact"].includes(event.currentTarget.value)
                ? { notation: event.currentTarget.value, scale: "none" }
                : { notation: "standard", scale: event.currentTarget.value },
            )
          }
        >
          <option value="standard">{labels.standard}</option>
          <option value="compact">{labels.compact}</option>
          <option value="thousands">{labels.thousands}</option>
          <option value="millions">{labels.millions}</option>
          <option value="billions">{labels.billions}</option>
        </select>
      </label>
      <label>
        <span>{labels.layout}</span>
        <select
          className="datox-react-dashboard__select"
          value={style.kpiLayout || "standard"}
          onChange={(event) => props.onChange({ kpiLayout: event.currentTarget.value })}
        >
          <option value="standard">{labels.standard}</option>
          <option value="compact">{labels.compact}</option>
          <option value="prominent">{labels.prominent}</option>
        </select>
      </label>
      <label>
        <span>{labels.target}</span>
        <select
          className="datox-react-dashboard__select"
          value={target?.visual || "none"}
          onChange={(event) =>
            props.onChange({
              target:
                event.currentTarget.value === "none"
                  ? null
                  : { value: target?.value ?? 1, visual: event.currentTarget.value },
            })
          }
        >
          <option value="none">{labels.none}</option>
          <option value="bar">{labels.bar}</option>
          <option value="ring">{labels.ring}</option>
        </select>
      </label>
      {target ? (
        <label>
          <span>{labels.targetValue}</span>
          <small>{labels.targetHelp}</small>
          <input
            className="datox-react-dashboard__input"
            type="number"
            step="any"
            min="0"
            aria-invalid={!Number.isFinite(target.value) || target.value <= 0}
            value={target.value ?? ""}
            onChange={(event) =>
              props.onChange({
                target: {
                  ...target,
                  value: event.currentTarget.value === "" ? null : Number(event.currentTarget.value),
                },
              })
            }
          />
        </label>
      ) : null}
      {resolveDashboardKpi(0, style).status === "invalid" ? <p role="alert">{labels.invalid}</p> : null}
    </>
  )
}

export function KpiFormulaSettings(props) {
  const kpi = useContext(DashboardKpiContext)
  const labels = props.labels.kpi
  const saved = props.widget.kpi_calculation
  const id = useId()
  const [mode, setMode] = useState(saved ? "formula" : "aggregation")
  const [expression, setExpression] = useState(saved?.expression || "")
  const [preview, setPreview] = useState(null)
  const key = dashboardKpiRequestKey(
    {
      ...props.widget,
      kpi_calculation: mode === "formula" ? { version: 1, mode: "formula", expression } : null,
    },
    props.filterWidgets || [],
    kpi.sourceKeys?.[props.widget.data?.dataset_id] ?? kpi.sourceKey,
  )
  useEffect(() => {
    setMode(saved ? "formula" : "aggregation")
    setExpression(saved?.expression || "")
  }, [props.widget.id, saved?.expression, kpi.dashboardID])
  useEffect(() => {
    if ((mode === "formula" && !expression.trim()) || !kpi.onEvaluateKpi) return undefined
    let active = true
    setPreview(null)
    const timer = setTimeout(() => {
      kpi
        .onEvaluateKpi(
          { ...props.widget, kpi_calculation: mode === "formula" ? { version: 1, mode: "formula", expression } : null },
          props.filterWidgets || [],
          0,
        )
        .then((result) => {
          if (active) setPreview({ key, result })
        })
        .catch(() => {
          if (active) setPreview({ key, result: { status: "error" } })
        })
    }, 300)
    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [key, mode])
  const result = preview?.key === key ? preview.result : null
  const previewText =
    result?.status === "ready"
      ? resolveDashboardKpi(result.value, props.widget.style || {}, kpi.locale).displayValue || labels.invalid
      : result?.status === "invalid"
        ? kpiIssueMessage(labels, result.error)
        : result?.status === "unavailable"
          ? labels.unavailable
          : result?.status === "error"
            ? labels.error
            : mode === "formula" && !expression.trim()
              ? labels.expression_required
              : labels.loading
  const previewDisplay = (
    <div role={result?.status === "invalid" || result?.status === "error" ? "alert" : "status"}>
      {labels.preview}: {previewText}
    </div>
  )
  return (
    <div className="datox-react-dashboard__chart-data-editor datox-kpi-formula-settings">
      <span className="datox-react-dashboard__field-label">{labels.calculation}</span>
      <KpiModeTabs
        id={id}
        label={labels.calculation}
        panelID={`${id}-panel`}
        value={mode}
        options={[
          { value: "aggregation", label: labels.aggregation },
          { value: "formula", label: labels.formula },
        ]}
        onChange={(next) => {
          setMode(next)
          if (next === "aggregation" && saved) props.onChange(null)
        }}
      />
      <div id={`${id}-panel`} role="tabpanel" aria-labelledby={`${id}-${mode}`}>
        {mode === "formula" ? (
          <>
            <KpiFormulaEditor
              key={`${kpi.dashboardID || ""}:${props.widget.id}`}
              value={expression}
              onChange={setExpression}
              columns={props.columns || []}
              labels={labels}
            />
            <div className="datox-kpi-formula__preview">{previewDisplay}</div>
            <button
              type="button"
              className="datox-react-dashboard__calculation-button"
              disabled={
                !result || !["ready", "unavailable"].includes(result.status) || expression === saved?.expression
              }
              onClick={() => props.onChange({ version: 1, mode: "formula", expression })}
            >
              {labels.apply}
            </button>
          </>
        ) : (
          <>
            {props.children}
            {previewDisplay}
          </>
        )}
      </div>
    </div>
  )
}
